import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageUploadField from "./ImageUploadField";

const QUILL_MINI = {
  toolbar: [["bold", "italic", "underline"], ["link"], [{ list: "ordered" }, { list: "bullet" }]],
};

const fieldSx = {
  mb: 1.5,
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.6)" },
};

function RichField({ label, value, onChange }) {
  return (
    <Box className="clientCommsQuillMini" sx={{ mb: 1.5 }}>
      <style>{`
        .clientCommsQuillMini .ql-toolbar { background: #2a2a3e; border-color: rgba(255,255,255,0.15) !important; border-radius: 8px 8px 0 0; }
        .clientCommsQuillMini .ql-toolbar .ql-stroke { stroke: rgba(255,255,255,0.7); }
        .clientCommsQuillMini .ql-toolbar .ql-fill { fill: rgba(255,255,255,0.7); }
        .clientCommsQuillMini .ql-toolbar .ql-picker { color: rgba(255,255,255,0.7); }
        .clientCommsQuillMini .ql-container { background: #161625; border-color: rgba(255,255,255,0.1) !important; border-radius: 0 0 8px 8px; min-height: 120px; }
        .clientCommsQuillMini .ql-editor { color: rgba(255,255,255,0.9); font-size: 14px; min-height: 110px; }
        .clientCommsQuillMini .ql-editor.ql-blank::before { color: rgba(255,255,255,0.35); }
      `}</style>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.75 }}>{label}</Typography>
      <ReactQuill theme="snow" value={value || ""} onChange={onChange} modules={QUILL_MINI} />
    </Box>
  );
}

const BlockForm = ({ block, onChange }) => {
  if (!block) {
    return (
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
        Select a block to edit, or add one below.
      </Typography>
    );
  }

  const patch = (partial) => onChange({ ...block, ...partial });

  switch (block.type) {
    case "hero":
      return (
        <Box>
          <ImageUploadField label="Hero image (full width)" value={block.url} onChange={(url) => patch({ url })} />
          <TextField fullWidth size="small" label="Alt text" value={block.alt || ""} onChange={(e) => patch({ alt: e.target.value })} sx={fieldSx} />
          <TextField fullWidth size="small" label="Optional link (https)" value={block.link || ""} onChange={(e) => patch({ link: e.target.value })} sx={fieldSx} />
        </Box>
      );
    case "heading":
      return (
        <Box>
          <TextField fullWidth size="small" label="Eyebrow (optional)" value={block.eyebrow || ""} onChange={(e) => patch({ eyebrow: e.target.value })} sx={fieldSx} />
          <TextField fullWidth size="small" label="Title" value={block.title || ""} onChange={(e) => patch({ title: e.target.value })} sx={fieldSx} />
          <TextField fullWidth size="small" multiline minRows={3} label="Lead paragraph" value={block.lead || ""} onChange={(e) => patch({ lead: e.target.value })} sx={fieldSx} />
        </Box>
      );
    case "greeting":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={block.enabled !== false}
              onChange={(e) => patch({ enabled: e.target.checked })}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#2af598" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2af598" } }}
            />
          }
          label={<Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>Include “Dear first name,”</Typography>}
        />
      );
    case "text":
      return <RichField label="Paragraph" value={block.html} onChange={(html) => patch({ html })} />;
    case "note":
      return <RichField label="Footnote (small print)" value={block.html} onChange={(html) => patch({ html })} />;
    case "section":
      return (
        <Box>
          <TextField fullWidth size="small" label="Section title" value={block.title || ""} onChange={(e) => patch({ title: e.target.value })} sx={fieldSx} />
          <RichField label="Section body" value={block.html} onChange={(html) => patch({ html })} />
        </Box>
      );
    case "facts": {
      const items = block.items || [];
      return (
        <Box>
          {items.map((it, idx) => (
            <Box key={idx} sx={{ mb: 1.5, p: 1.25, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 1 }}>
              <TextField fullWidth size="small" label="Label" value={it.label || ""} onChange={(e) => {
                const next = items.map((row, i) => (i === idx ? { ...row, label: e.target.value } : row));
                patch({ items: next });
              }} sx={fieldSx} />
              <TextField fullWidth size="small" label="Value" value={it.value || ""} onChange={(e) => {
                const next = items.map((row, i) => (i === idx ? { ...row, value: e.target.value } : row));
                patch({ items: next });
              }} sx={fieldSx} />
              <TextField fullWidth size="small" label="Note" value={it.note || ""} onChange={(e) => {
                const next = items.map((row, i) => (i === idx ? { ...row, note: e.target.value } : row));
                patch({ items: next });
              }} sx={fieldSx} />
              {items.length > 1 ? (
                <Button size="small" onClick={() => patch({ items: items.filter((_, i) => i !== idx) })} sx={{ color: "rgba(255,255,255,0.55)", textTransform: "none" }}>
                  Remove fact
                </Button>
              ) : null}
            </Box>
          ))}
          {items.length < 3 ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => patch({ items: [...items, { label: "", value: "", note: "" }] })}
              sx={{ borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.85)", textTransform: "none" }}
            >
              Add fact
            </Button>
          ) : null}
        </Box>
      );
    }
    case "button":
      return (
        <Box>
          <TextField fullWidth size="small" label="Button label" value={block.label || ""} onChange={(e) => patch({ label: e.target.value })} sx={fieldSx} />
          <TextField fullWidth size="small" label="URL (https)" value={block.url || ""} onChange={(e) => patch({ url: e.target.value })} sx={fieldSx} />
          <FormControl>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.5 }}>Alignment</Typography>
            <RadioGroup row value={block.align || "center"} onChange={(e) => patch({ align: e.target.value })}>
              <FormControlLabel
                value="center"
                control={<Radio sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#2af598" } }} />}
                label={<Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>Center</Typography>}
              />
              <FormControlLabel
                value="left"
                control={<Radio sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#2af598" } }} />}
                label={<Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>Left</Typography>}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      );
    case "image":
      return (
        <Box>
          <ImageUploadField label="Inline image" value={block.url} onChange={(url) => patch({ url })} />
          <TextField fullWidth size="small" label="Alt text" value={block.alt || ""} onChange={(e) => patch({ alt: e.target.value })} sx={fieldSx} />
          <TextField fullWidth size="small" label="Caption" value={block.caption || ""} onChange={(e) => patch({ caption: e.target.value })} sx={fieldSx} />
        </Box>
      );
    case "divider":
      return <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>Thin divider — no options.</Typography>;
    default:
      return null;
  }
};

export default BlockForm;
